/**
 * Settings Store
 * 管理游戏系统配置 (背景音乐、视频、音效、筹码等)
 */

import { defineStore } from 'pinia'
import localforage from 'localforage'

interface SettingsState {
  // 背景音乐开关
  playBgMusic: boolean
  // 背景音乐列表
  bgmList: string[]
  // 背景音乐当前播放索引
  bgmCurrentIndex: number
  // 视频开关
  playVideo: boolean
  // 游戏音效开关
  playGameSound: boolean
  // 当前选中的视频线路ID ('0'=电信, '1'=网通, '2'=移动)
  currentVideoCenterId: string
  // 视频线路切换版本号 (用于强制刷新视频，即使 URL 相同)
  videoLineVersion: number
  // 筹码列表 (当前可用筹码)
  chipList: number[]
  // 当前选中的筹码
  currentChip: number
  // 当前选中的游戏类型 (BJL/JSBJL/LHD/SNN/ZJH/GZB)
  activeGameType: string
}

// 所有可用的筹码面额 (固定不变)
const ALL_CHIP_LIST = [10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000]

export const useSettingsStore = defineStore('settings', {
  state: (): SettingsState => ({
    playBgMusic: false,
    bgmList: [],
    bgmCurrentIndex: 0,
    playVideo: true,
    playGameSound: true,
    currentVideoCenterId: '0', // 默认: 电信
    videoLineVersion: 0, // 视频线路版本号
    chipList: [...ALL_CHIP_LIST],
    currentChip: 10,
    activeGameType: 'BJL', // 默认: 百家乐
  }),

  getters: {
    /**
     * 所有可用的筹码面额列表 (固定不变)
     */
    allChipList: () => ALL_CHIP_LIST,

    /**
     * 是否启用背景音乐
     */
    isBgMusicEnabled: state => state.playBgMusic,

    /**
     * 是否启用视频
     */
    isVideoEnabled: state => state.playVideo,

    /**
     * 是否启用游戏音效
     */
    isGameSoundEnabled: state => state.playGameSound,
  },

  actions: {
    /**
     * 设置背景音乐开关
     */
    setPlayBgMusic(enabled: boolean) {
      this.playBgMusic = enabled
      this.persistSettings()
    },

    /**
     * 设置背景音乐列表
     */
    setBgmList(list: string[]) {
      this.bgmList = list
      this.persistSettings()
    },

    /**
     * 设置背景音乐当前播放索引
     */
    setBgmCurrentIndex(index: number) {
      this.bgmCurrentIndex = index
      this.persistSettings()
    },

    /**
     * 设置视频开关
     */
    setPlayVideo(enabled: boolean) {
      this.playVideo = enabled
      this.persistSettings()
    },

    /**
     * 设置游戏音效开关
     * 注意: 音效开关不持久化，每次打开网站都默认开启
     */
    setPlayGameSound(enabled: boolean) {
      this.playGameSound = enabled
      // 不调用 persistSettings()，音效开关不持久化

      // 如果重新开启音效,重置失败状态
      if (enabled) {
        import('@/services/sound-manager').then(({ resetSoundFailureState }) => {
          resetSoundFailureState()
        })
      }
    },

    /**
     * 设置筹码列表
     */
    setChipList(list: number[]) {
      this.chipList = list
      this.persistSettings()
    },

    /**
     * 设置当前选中的筹码
     */
    setCurrentChip(chip: number) {
      this.currentChip = chip
      this.persistSettings()
    },

    /**
     * 设置当前选中的视频线路ID
     * 同时增加版本号，强制视频重新加载 (即使 URL 相同)
     *
     * @param videoCenterId - 视频中心ID ('0'=电信, '1'=网通, '2'=移动)
     */
    setCurrentVideoCenterId(videoCenterId: string) {
      this.currentVideoCenterId = videoCenterId
      this.videoLineVersion++ // 增加版本号，强制触发视频刷新
      this.persistSettings()
    },

    /**
     * 设置当前选中的游戏类型
     *
     * @param gameType - 游戏类型 (BJL/JSBJL/LHD/SNN/ZJH/GZB)
     */
    setActiveGameType(gameType: string) {
      this.activeGameType = gameType
      this.persistSettings()
    },

    /**
     * 持久化设置到 IndexedDB
     */
    async persistSettings() {
      try {
        // 使用 toRaw() 将 Proxy 对象转换为普通对象,避免 IndexedDB DataCloneError
        // 注意: playGameSound 不持久化，每次打开网站都默认开启
        const settings = {
          playBgMusic: this.playBgMusic,
          bgmList: toRaw(this.bgmList), // 转换 Proxy 数组
          bgmCurrentIndex: this.bgmCurrentIndex,
          playVideo: this.playVideo,
          // playGameSound 不持久化
          currentVideoCenterId: this.currentVideoCenterId,
          chipList: toRaw(this.chipList), // 转换 Proxy 数组
          currentChip: this.currentChip,
          activeGameType: this.activeGameType,
          updatedAt: Date.now(),
        }

        await localforage.setItem('gameSettings', settings)
      } catch (error) {
        console.error('[Settings] 持久化设置失败:', error)
      }
    },

    /**
     * 从 IndexedDB 恢复设置
     */
    async restoreFromCache() {
      try {
        const cached = await localforage.getItem<{
          playBgMusic: boolean
          bgmList: string[]
          bgmCurrentIndex: number
          playVideo: boolean
          playGameSound: boolean
          currentVideoCenterId: string
          chipList: number[]
          currentChip: number
          activeGameType: string
          updatedAt: number
        }>('gameSettings')

        if (cached) {
          this.playBgMusic = cached.playBgMusic ?? false
          this.bgmList = cached.bgmList ?? []
          this.bgmCurrentIndex = cached.bgmCurrentIndex ?? 0
          this.playVideo = cached.playVideo ?? true
          // 不恢复 playGameSound，每次打开网站都使用默认值 true
          // this.playGameSound = cached.playGameSound ?? true
          this.currentVideoCenterId = cached.currentVideoCenterId ?? '0'
          this.chipList = cached.chipList ?? [...ALL_CHIP_LIST]
          this.currentChip = cached.currentChip ?? 10
          this.activeGameType = cached.activeGameType ?? 'BJL'
          return true
        }

        return false
      } catch (error) {
        console.error('[Settings] 恢复设置失败:', error)
        return false
      }
    },

    /**
     * 重置所有设置为默认值
     */
    async resetToDefaults() {
      this.playBgMusic = false
      this.bgmList = []
      this.bgmCurrentIndex = 0
      this.playVideo = true
      this.playGameSound = true
      this.currentVideoCenterId = '0'
      this.chipList = [...ALL_CHIP_LIST]
      this.currentChip = 10
      this.activeGameType = 'BJL'

      await this.persistSettings()
    },

    /**
     * 清空设置 (注意: 游戏设置不应该在退出登录时清空,因为这是用户偏好)
     *
     * 这个方法只在用户主动要求重置设置时调用
     */
    async clear() {
      await this.resetToDefaults()
      await localforage.removeItem('gameSettings')
    },
  },
})
